import React, { useState, useEffect } from 'react';
import { Plus, Edit2, X, Search, ChevronDown, ChevronLeft, ChevronRight, Download, Edit } from 'lucide-react';
import TimeSelector from './TimeSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function DocenteLicencias() {
  const currentYear = new Date().getFullYear();
  const [selectedGestion, setSelectedGestion] = useState(currentYear);
  const [licencias, setLicencias] = useState([]);
  const [asignaturas, setAsignaturas] = useState([]);
  const [docentes, setDocentes] = useState([]);
  const [salas, setSalas] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingLicencia, setEditingLicencia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('personal'); // 'personal' or 'student'
  const [estudianteLicencias, setEstudianteLicencias] = useState([]);
  const [loadingEstudiantes, setLoadingEstudiantes] = useState(false);

  const [formData, setFormData] = useState({
    id_asignatura: '',
    paralelo: '',
    tipo_permiso: 'reemplazo',
    fecha_permiso_desde: '',
    fecha_permiso_hasta: '',
    hora_permiso_inicio: '',
    hora_permiso_fin: '',
    id_docente_reemplazo: '',
    docente_reemplazo_otro: '',
    docente_reemplazo_otro_ci: '',
    fecha_reemplazo_desde: '',
    fecha_reemplazo_hasta: '',
    hora_reemplazo_inicio: '',
    hora_reemplazo_fin: '',
    id_sala_compensacion: '',
    fecha_compensacion_desde: '',
    fecha_compensacion_hasta: '',
    hora_compensacion_inicio: '',
    hora_compensacion_fin: ''
  });

  useEffect(() => {
    loadLicencias();
    loadEstudianteLicencias();
    loadAsignaturas();
    loadDocentes();
    loadSalas();
  }, [selectedGestion]);

  const loadEstudianteLicencias = async () => {
    try {
      setLoadingEstudiantes(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/docente/licencias-estudiantes?gestion=${selectedGestion}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setEstudianteLicencias(data.licencias || []);
    } catch (error) {
      console.error('Error loading student licencias:', error);
      setEstudianteLicencias([]);
    } finally {
      setLoadingEstudiantes(false);
    }
  };

  const loadLicencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/docente/licencias?gestion=${selectedGestion}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setLicencias(data.licencias || []);
    } catch (error) {
      console.error('Error loading licencias:', error);
      setLicencias([]);
    }
  };

  const loadAsignaturas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch regular and preadmision subjects in parallel
      const [resReg, resPre] = await Promise.all([
        fetch(`${API_URL}/docente/asignaturas`, { headers }),
        fetch(`${API_URL}/docente/asignaturas?tipo=preadmision`, { headers })
      ]);

      const dataReg = await resReg.json();
      const dataPre = await resPre.json();

      // Combine both lists
      const combined = [
        ...(dataReg.asignaturas || []),
        ...(dataPre.asignaturas || [])
      ];

      setAsignaturas(combined);
    } catch (error) {
      console.error('Error loading asignaturas:', error);
      setAsignaturas([]);
    }
  };

  const loadDocentes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/docente/docentes-reemplazo`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setDocentes(data.docentes || []);
    } catch (error) {
      console.error('Error loading docentes:', error);
      setDocentes([]);
    }
  };

  const loadSalas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/docente/salas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setSalas(data.salas || []);
    } catch (error) {
      console.error('Error loading salas:', error);
      setSalas([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'asignatura_paralelo') {
      const parts = value.split('|');
      setFormData(prev => ({
        ...prev,
        id_asignatura: parts[0] || '',
        paralelo: parts[1] || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   try {
  //     const url = editingLicencia
  //       ? `${API_URL}/docente/licencias/${editingLicencia.id_licencia}`
  //       : `${API_URL}/docente/licencias`;

  //     const method = editingLicencia ? 'PUT' : 'POST';

  //     const token = localStorage.getItem('token');
  //     const response = await fetch(url, {
  //       method,
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `Bearer ${token}`
  //       },
  //       body: JSON.stringify(formData)
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       alert(data.message);
  //       setShowModal(false);
  //       resetForm();
  //       await loadLicencias();
  //     } else {
  //       alert(data.message || 'Error al guardar licencia');
  //     }
  //   } catch (error) {
  //     console.error('Error saving licencia:', error);
  //     alert('Error al guardar licencia');
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      id_asignatura,
      fecha_permiso_desde,
      hora_permiso_inicio,
      hora_permiso_fin
    } = formData;

    const horaInicioForm = hora_permiso_inicio?.slice(0, 5).trim();
    const horaFinForm = hora_permiso_fin?.slice(0, 5).trim();

    const fechaFormLimpia = fecha_permiso_desde ? new Date(fecha_permiso_desde).toISOString().split('T')[0] : '';
    const idAsignaturaForm = String(id_asignatura || '').trim();

    const registroConflictivo = licencias.find(l => {
      if (editingLicencia && String(l.id_licencia) === String(editingLicencia.id_licencia)) {
        return false;
      }

      const idAsignaturaTabla = String(l.id_asignatura || '').trim();
      const horaInicioTabla = l.hora_permiso_inicio?.slice(0, 5).trim();
      const horaFinTabla = l.hora_permiso_fin?.slice(0, 5).trim();

      const fechaTablaLimpia = l.fecha_permiso_desde ? new Date(l.fecha_permiso_desde).toISOString().split('T')[0] : '';

      return (
        idAsignaturaTabla === idAsignaturaForm &&
        fechaTablaLimpia === fechaFormLimpia &&
        horaInicioTabla === horaInicioForm &&
        horaFinTabla === horaFinForm
      );
    });

    if (registroConflictivo) {

      const [anio, mes, dia] = fechaFormLimpia.split('-');
      const fechaFormateada = `${dia}/${mes}/${anio}`;

      alert(
        `NO SE PUEDE REGISTRAR\n\n` +
        `Ya existe un permiso ingresado para esta asignatura el día ${fechaFormateada} ` +
        `en el horario de ${horaInicioForm} a ${horaFinForm}.`
      );
      return;
    }

    try {
      const url = editingLicencia
        ? `${API_URL}/docente/licencias/${editingLicencia.id_licencia}`
        : `${API_URL}/docente/licencias`;

      const method = editingLicencia ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Solicitud enviada exitosamente');
        setShowModal(false);
        resetForm();
        await loadLicencias();
      } else {
        alert(data.message || 'Error al guardar licencia');
      }
    } catch (error) {
      console.error('Error saving licencia:', error);
      alert('Error de red al guardar licencia');
    }
  };

  const handleDownloadSingle = async (lic, type = 'personal') => {
    try {
      const token = localStorage.getItem('token');
      const id = lic.id_licencia;
      const endpoint = type === 'student'
        ? `${API_URL}/docente/licencias-estudiantes/descargar-excel?id=${id}`
        : `${API_URL}/docente/licencias/descargar-excel?id=${id}`;

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        alert('Error al descargar el archivo Excel');
        return;
      }

      const arrayBuffer = await response.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'student'
        ? `Licencia_Estudiante_${lic.estudiante_codigo || 'ID'}.xlsx`
        : `Solicitud_Permiso_${lic.fecha_permiso_desde?.slice(0, 10) || 'Docente'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading individual license:', error);
      alert('Error al procesar la descarga');
    }
  };

const handleEdit = (licencia) => {
  setEditingLicencia(licencia);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().slice(0, 10); } catch (e) { return ''; }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.slice(0, 5);
  };

  // ✅ Fix: lógica correcta para determinar el valor del select de docente
  const docenteReemplazoVal = licencia.id_docente_reemplazo
    ? String(licencia.id_docente_reemplazo)
    : (licencia.docente_reemplazo_otro || licencia.docente_reemplazo_otro_ci)
      ? 'otro'
      : '';

  // ✅ Fix: buscar el paralelo exacto que usa el select
  const asigMatch = asignaturas.find(
    a => String(a.id_asignatura) === String(licencia.id_asignatura)
  );
  const paraleloReal = asigMatch?.paralelo || licencia.paralelo || '';

  setFormData({
    id_asignatura: licencia.id_asignatura || '',
    paralelo: paraleloReal,
    tipo_permiso: licencia.tipo_permiso || 'reemplazo',
    fecha_permiso_desde: formatDate(licencia.fecha_permiso_desde),
    fecha_permiso_hasta: formatDate(licencia.fecha_permiso_hasta),
    hora_permiso_inicio: formatTime(licencia.hora_permiso_inicio),
    hora_permiso_fin: formatTime(licencia.hora_permiso_fin),
    id_docente_reemplazo: docenteReemplazoVal,
    docente_reemplazo_otro: licencia.docente_reemplazo_otro || '',
    docente_reemplazo_otro_ci: licencia.docente_reemplazo_otro_ci || '',
    fecha_reemplazo_desde: formatDate(licencia.fecha_reemplazo_desde),
    fecha_reemplazo_hasta: formatDate(licencia.fecha_reemplazo_hasta),
    hora_reemplazo_inicio: formatTime(licencia.hora_reemplazo_inicio),
    hora_reemplazo_fin: formatTime(licencia.hora_reemplazo_fin),
    id_sala_compensacion: licencia.id_sala_compensacion || '',
    fecha_compensacion_desde: formatDate(licencia.fecha_compensacion_desde),
    fecha_compensacion_hasta: formatDate(licencia.fecha_compensacion_hasta),
    hora_compensacion_inicio: formatTime(licencia.hora_compensacion_inicio),
    hora_compensacion_fin: formatTime(licencia.hora_compensacion_fin),
  });

  setShowModal(true);
};

  const resetForm = () => {
    setEditingLicencia(null);
    setFormData({
      id_asignatura: '',
      paralelo: '',
      tipo_permiso: 'reemplazo',
      fecha_permiso_desde: '',
      fecha_permiso_hasta: '',
      hora_permiso_inicio: '',
      hora_permiso_fin: '',
      id_docente_reemplazo: '',
      docente_reemplazo_otro: '',
      docente_reemplazo_otro_ci: '',
      fecha_reemplazo_desde: '',
      fecha_reemplazo_hasta: '',
      hora_reemplazo_inicio: '',
      hora_reemplazo_fin: '',
      id_sala_compensacion: '',
      fecha_compensacion_desde: '',
      fecha_compensacion_hasta: '',
      hora_compensacion_inicio: '',
      hora_compensacion_fin: ''
    });
  };

  const getEstadoBadge = (estado) => {
    const s = (estado || '').toLowerCase().trim();
    if (s === 'pendiente') return 'bg-yellow-100 text-yellow-800';
    if (s === 'aprobado') return 'bg-green-100 text-green-800';
    if (s === 'aprobado direccion' || s === 'aprobado_direccion') {
      return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
    }
    if (s === 'observado') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="">
          <div className="flex items-center justify-between">
            <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-800">Docente &gt; Solicitud Permisos</h1>
          </div>
            
            <div className="flex items-center gap-3">
              <select
                value={selectedGestion}
                onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
                className="px-4 py-2 border border-gray-300 rounded-full bg-white"
              >
                {[2024, 2025, 2026, 2027].map(g => (
                  <option key={g} value={g}>Gestión {g}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  resetForm();
                  setShowModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
              >
                <Plus size={18} />
                Añadir Licencia
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4 bg-white rounded-t-lg px-4 pt-4">
          <button
            onClick={() => setActiveTab('personal')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'personal'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            Mis Permisos
          </button>
        </div>

        {/* Tabla de Licencias */}
        <div className="bg-white rounded-b-lg shadow-sm border-t-0 overflow-hidden">
          {activeTab === 'personal' ? (
            <>
              {licencias.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No hay licencias registradas</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-center font-semibold border">No</th>
                        <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                        <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                        <th className="px-3 py-3 text-center font-semibold border">Tipo Permiso</th>
                        <th className="px-3 py-3 text-center font-semibold border">Fecha Permiso</th>
                        <th className="px-3 py-3 text-center font-semibold border">Hora Permiso</th>
                        <th className="px-3 py-3 text-center font-semibold border">Reemplazo/Sala</th>
                        <th className="px-3 py-3 text-center font-semibold border">Estado Jefatura</th>
                        <th className="px-3 py-3 text-center font-semibold border">Observaciones</th>
                        <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {licencias.map((lic, idx) => (
                        <tr key={lic.id_licencia} className="hover:bg-gray-50">
                          <td className="p-3 border">{idx + 1}</td>
                          <td className="p-3 text-sm font-medium border">{lic.asignatura_nombre || '-'}</td>
                          <td className="p-3 text-sm border">{lic.paralelo || '-'}</td>
                          <td className="p-3 text-sm border">
                            <span className={`px-2 py-1 rounded-full text-xs ${lic.tipo_permiso === 'reemplazo' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`}>
                              {lic.tipo_permiso}
                            </span>
                          </td>
                          <td className="p-3 text-sm border">
                            {lic.fecha_permiso_desde && lic.fecha_permiso_hasta
                              ? `${new Date(lic.fecha_permiso_desde).toLocaleDateString()} - ${new Date(lic.fecha_permiso_hasta).toLocaleDateString()}`
                              : '-'}
                          </td>
                          <td className="p-3 text-sm border">
                            {lic.hora_permiso_inicio && lic.hora_permiso_fin
                              ? `${lic.hora_permiso_inicio} - ${lic.hora_permiso_fin}`
                              : '-'}
                          </td>
                          <td className="p-3 text-sm border">
                            {lic.tipo_permiso === 'reemplazo'
                              ? lic.docente_reemplazo_nombre || '-'
                              : lic.sala_compensacion_nombre || '-'}
                          </td>
                          <td className="p-3 text-sm border">
                            <span className={`px-2 py-1 rounded-full text-xs ${getEstadoBadge(lic.estado)}`}>
                              {lic.estado}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-600 max-w-xs truncate border">
                            {lic.observaciones_jefatura || '-'}
                          </td>
                          <td className="p-3 border">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => handleEdit(lic)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="Editar licencia"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDownloadSingle(lic, 'personal')}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Descargar Excel individual"
                              >
                                <Download size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <>
              {loadingEstudiantes ? (
                <div className="p-8 text-center text-gray-500">Cargando licencias de estudiantes...</div>
              ) : estudianteLicencias.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No hay licencias de estudiantes registradas para sus materias</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-center font-semibold border">No</th>
                        <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                        <th className="px-3 py-3 text-center font-semibold border">Código</th>
                        <th className="px-3 py-3 text-center font-semibold border">Nombre</th>
                        <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                        <th className="px-3 py-3 text-center font-semibold border">Desde</th>
                        <th className="px-3 py-3 text-center font-semibold border">Hasta</th>
                        <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                        <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estudianteLicencias.map((lic, idx) => (
                        <tr key={lic.id_licencia || idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-sm">{idx + 1}</td>
                          <td className="p-3 text-sm font-medium">{lic.asignatura || '-'}</td>
                          <td className="p-3 text-sm font-mono text-blue-600">{lic.estudiante_codigo || lic.asignatura_codigo || '-'}</td>
                          <td className="p-3 text-sm font-medium">{lic.estudiante_nombre || '-'}</td>
                          <td className="p-3 text-sm">{lic.paralelo || 'A'}</td>
                          <td className="p-3 text-sm">
                            {lic.fecha_hora_inicio
                              ? new Date(lic.fecha_hora_inicio).toLocaleDateString('es-BO')
                              : lic.fecha_licencia
                                ? new Date(lic.fecha_licencia).toLocaleDateString('es-BO')
                                : '-'}
                          </td>
                          <td className="p-3 text-sm">
                            {lic.fecha_hora_fin
                              ? new Date(lic.fecha_hora_fin).toLocaleDateString('es-BO')
                              : lic.fecha_licencia
                                ? new Date(lic.fecha_licencia).toLocaleDateString('es-BO')
                                : '-'}
                          </td>
                          <td className="p-3 text-sm">
                            {(() => {
                              const s = (lic.estado || '').toLowerCase().trim();
                              if (s.includes('aprobado')) {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
                                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    Aprobada por Jefatura
                                  </span>
                                );
                              }
                              if (s.includes('observado') || s.includes('rechazado')) {
                                return (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {lic.estado}
                                  </span>
                                );
                              }
                              return (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  {lic.estado || 'Pendiente'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="p-3">
                            <div className="flex justify-center">
                              <button
                                onClick={() => handleDownloadSingle(lic, 'student')}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="Descargar Excel individual"
                              >
                                <Download size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">
                    {editingLicencia ? 'Editar Licencia' : 'Nueva Solicitud de Licencia'}
                  </h3>
                  <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                    <X size={24} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Datos Básicos */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Asignatura *</label>
                      <select
                        name="asignatura_paralelo"
                        value={formData.id_asignatura ? `${formData.id_asignatura}|${formData.paralelo || ''}` : ''}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar</option>
                        {asignaturas.map(a => {
                          const val = `${a.id_asignatura}|${a.paralelo || ''}`;
                          return (
                            <option key={`${a.id_asignatura}-${a.paralelo || 'nada'}`} value={val}>
                              {a.nombre} {a.paralelo ? `(${a.paralelo})` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Permiso *</label>
                      <select
                        name="tipo_permiso"
                        value={formData.tipo_permiso}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="reemplazo">Por Reemplazo</option>
                        <option value="compensacion">Por Compensación</option>
                      </select>
                    </div>
                  </div>

                  {/* Datos del Permiso */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-800 mb-3">Datos del Permiso</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Desde *</label>
                        <input
                          type="date"
                          name="fecha_permiso_desde"
                          value={formData.fecha_permiso_desde}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hasta *</label>
                        <input
                          type="date"
                          name="fecha_permiso_hasta"
                          value={formData.fecha_permiso_hasta}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio *</label>
                        <TimeSelector
                          name="hora_permiso_inicio"
                          value={formData.hora_permiso_inicio}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin *</label>
                        <TimeSelector
                          name="hora_permiso_fin"
                          value={formData.hora_permiso_fin}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Datos de Reemplazo (si aplica) */}
                  {formData.tipo_permiso === 'reemplazo' && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Datos del Reemplazo</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Docente Reemplazo</label>
                          <select
                            name="id_docente_reemplazo"
                            value={formData.id_docente_reemplazo}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Seleccionar</option>
                            {docentes.map(d => (
                              <option key={d.id_docente} value={d.id_docente}>
                                {d.nombre_completo || `${d.nombres} ${d.apellidos}`}
                              </option>
                            ))}
                            <option value="otro">Otro</option>
                          </select>
                        </div>
                        {formData.id_docente_reemplazo === 'otro' && (<>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Docente de Reemplazo</label>
                            <input
                              type="text"
                              name="docente_reemplazo_otro"
                              value={formData.docente_reemplazo_otro}
                              onChange={handleChange}
                              placeholder="Escribir nombre completo del docente"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">CI del Docente</label>
                            <input
                              type="text"
                              name="docente_reemplazo_otro_ci"
                              value={formData.docente_reemplazo_otro_ci || ''}
                              onChange={handleChange}
                              placeholder="Número de CI"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Reemplazo Desde</label>
                          <input
                            type="date"
                            name="fecha_reemplazo_desde"
                            value={formData.fecha_reemplazo_desde}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Reemplazo Hasta</label>
                          <input
                            type="date"
                            name="fecha_reemplazo_hasta"
                            value={formData.fecha_reemplazo_hasta}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio</label>
                          <TimeSelector
                            name="hora_reemplazo_inicio"
                            value={formData.hora_reemplazo_inicio}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin</label>
                          <TimeSelector
                            name="hora_reemplazo_fin"
                            value={formData.hora_reemplazo_fin}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Datos de Compensación (si aplica) */}
                  {formData.tipo_permiso === 'compensacion' && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Datos de Compensación</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Sala</label>
                          <select
                            name="id_sala_compensacion"
                            value={formData.id_sala_compensacion}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Seleccionar</option>
                            {salas.map(s => (
                              <option key={s.id_sala} value={s.id_sala}>
                                {s.nombre}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Compensación Desde</label>
                          <input
                            type="date"
                            name="fecha_compensacion_desde"
                            value={formData.fecha_compensacion_desde}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Compensación Hasta</label>
                          <input
                            type="date"
                            name="fecha_compensacion_hasta"
                            value={formData.fecha_compensacion_hasta}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio</label>
                          <TimeSelector
                            name="hora_compensacion_inicio"
                            value={formData.hora_compensacion_inicio}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin</label>
                          <TimeSelector
                            name="hora_compensacion_fin"
                            value={formData.hora_compensacion_fin}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
                    >
                      Enviar Solicitud
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}